import { z } from 'zod';

import { PAGINATION_SORT_DIRECTION } from '~/constants/pagination';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';

// Mirrors the API's `maxItems` on the id lists of the supplier overview filters.
const MAX_FILTER_IDS = 1000;

/**
 * Filters of the supplier overview, sent as the JSON body of the
 * `POST data_exchange/company_information/{query,query/count,excel}` requests.
 * The API rejects unknown fields and invalid UUIDs with a 422.
 */
export const zodSupplierOverviewFilters = z.object({
  companyAccountId: z.uuid().optional(),
  companyId: z.array(z.uuid()).max(MAX_FILTER_IDS).optional(),
  hasResponsiblePerson: z.boolean().optional(),
  responsiblePersonId: z.preprocess(
    // SuppliersFilter uses '' as the value of the 'all people' option; it is no valid UUID
    (value) => (Array.isArray(value) ? value.filter(Boolean) : value),
    z.array(z.uuid()).max(MAX_FILTER_IDS).optional(),
  ),
  searchText: z.string().trim().max(255).optional(),
  status: z
    .array(
      z.enum([
        'active',
        'live',
        'qr_code',
        'test_phase',
        'implementation_supplier',
        'implementation_vestigas',
        'test_data_exchanged',
        'confirmed',
        'on_hold_supplier',
        'in_qualification',
        'interested',
        'not_feasible',
      ]),
    )
    .optional(),
});

export const zodSupplierOverviewOrderBy = z.enum([
  'alphabetically',
  'invoice_status',
  'last_contact',
]);

/**
 * Sorting and paging of the supplier overview. They stay in the query string of
 * the POST requests.
 */
export const zodSupplierOverviewPagination = z.object({
  limit: z.number().gte(1).optional(),
  offset: z.number().gte(0).optional(),
  orderBy: zodSupplierOverviewOrderBy.optional(),
  sortOrder: z.enum(PAGINATION_SORT_DIRECTION).optional(),
});

export type SupplierOverviewParams = z.infer<
  typeof zodSupplierOverviewFilters
> &
  z.infer<typeof zodSupplierOverviewPagination>;

// The API rejects empty ids (422) and treats an absent filter as "no filter", so
// empty values are left out instead of being sent.
export const EMPTY_REQUEST_VALUES = [undefined, '', []];

/**
 * Splits the supplier overview parameters into the JSON body (filters) and the
 * query string (sorting and paging) expected by the POST endpoints.
 */
export const toSupplierOverviewRequest = ({
  limit,
  offset,
  orderBy,
  sortOrder,
  ...filters
}: SupplierOverviewParams) => ({
  json: withoutObjectKeysWhereValueIs(filters, EMPTY_REQUEST_VALUES),
  searchParams: withoutObjectKeysWhereValueIs(
    { limit, offset, orderBy, sortOrder },
    EMPTY_REQUEST_VALUES,
  ),
});
